// src/services/findingService.js

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  FINDING_STATUS,
  FINDING_STATUS_LABELS,
  getFindingKind,
} from "../constants/findings";
import { INCIDENT_EVENTS, recordIncidentEvent } from "./incidentEventService";

/**
 * problems/{problemId}/findings — the structured investigation record.
 *
 * Findings are editable by their author while the incident is live, because an
 * investigation is a working document and forcing immutability here would just
 * push experts back into free-text notes. The audit trail is preserved
 * separately: every create and every verdict change also appends an immutable
 * event, so the history of what was believed and when survives any later edit.
 */

function findingsRef(problemId) {
  return collection(db, "problems", problemId, "findings");
}

/**
 * @param {string} problemId
 * @param {object} input
 * @param {string} input.kind        FINDING_KIND value.
 * @param {string} input.text
 * @param {object} input.author      `{ id, name, role }`
 * @param {string[]} [input.evidenceIds]  Ids of files in problems/{id}/files.
 * @param {string} [input.runbookStepId]  Runbook step that produced it.
 */
export async function addFinding(problemId, input = {}) {
  const { kind, text, author, evidenceIds, runbookStepId } = input;
  const body = (text || "").trim();

  if (!problemId || !kind || !body) {
    return { success: false, error: "problemId, kind and text are required" };
  }

  const definition = getFindingKind(kind);

  try {
    const docRef = await addDoc(findingsRef(problemId), {
      kind: definition.id,
      text: body,
      // Only hypotheses carry a verdict, but storing the field uniformly keeps
      // queries and rules simple.
      status: FINDING_STATUS.OPEN,
      evidenceIds: Array.isArray(evidenceIds) ? evidenceIds : [],
      runbookStepId: runbookStepId || null,
      authorId: author?.id || null,
      authorName: author?.name || null,
      authorRole: author?.role || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await recordIncidentEvent(problemId, {
      type: INCIDENT_EVENTS.FINDING_RECORDED,
      actor: author,
      summary: `${definition.label}: ${body}`,
      metadata: { findingId: docRef.id, kind: definition.id },
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Could not add finding:", error);
    return { success: false, error: error.message };
  }
}

/** Records a verdict on a hypothesis. */
export async function setFindingStatus(problemId, finding, status, actor) {
  if (!problemId || !finding?.id) {
    return { success: false, error: "problemId and finding are required" };
  }

  try {
    await updateDoc(doc(db, "problems", problemId, "findings", finding.id), {
      status,
      updatedAt: serverTimestamp(),
    });

    await recordIncidentEvent(problemId, {
      type: INCIDENT_EVENTS.FINDING_RECORDED,
      actor,
      summary: `${FINDING_STATUS_LABELS[status]}: ${finding.text}`,
      metadata: {
        findingId: finding.id,
        kind: finding.kind,
        status,
      },
    });

    return { success: true };
  } catch (error) {
    console.error("Could not update finding status:", error);
    return { success: false, error: error.message };
  }
}

export async function updateFindingText(problemId, findingId, text) {
  const body = (text || "").trim();
  if (!problemId || !findingId || !body) {
    return { success: false, error: "problemId, findingId and text are required" };
  }

  try {
    await updateDoc(doc(db, "problems", problemId, "findings", findingId), {
      text: body,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error("Could not update finding:", error);
    return { success: false, error: error.message };
  }
}

export async function attachEvidenceToFinding(problemId, findingId, evidenceIds) {
  if (!problemId || !findingId) {
    return { success: false, error: "problemId and findingId are required" };
  }

  try {
    await updateDoc(doc(db, "problems", problemId, "findings", findingId), {
      evidenceIds: Array.isArray(evidenceIds) ? evidenceIds : [],
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error("Could not attach evidence to finding:", error);
    return { success: false, error: error.message };
  }
}

export async function deleteFinding(problemId, findingId) {
  if (!problemId || !findingId) {
    return { success: false, error: "problemId and findingId are required" };
  }

  try {
    await deleteDoc(doc(db, "problems", problemId, "findings", findingId));
    return { success: true };
  } catch (error) {
    console.error("Could not delete finding:", error);
    return { success: false, error: error.message };
  }
}

/** Subscribes to the investigation record, oldest first. */
export function subscribeToFindings(problemId, callback, onError) {
  if (!problemId) return () => {};

  return onSnapshot(
    query(findingsRef(problemId), orderBy("createdAt", "asc")),
    (snapshot) => {
      callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (error) => {
      console.error("Findings subscription error:", error);
      if (onError) onError(error);
      callback([]);
    }
  );
}
